import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './components/subscription/SubscriptionContext';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import CreateCDPage from './pages/CreateCDPage';
import ViewCDsPage from './pages/ViewCDsPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import ViewCDDetailPage from './pages/ViewCDDetailPage';
import SubscriptionDashboard from './components/subscription/SubscriptionDashboard';
import SubscriptionModal from './components/subscription/SubscriptionModal';
import Auth from './components/auth/Auth';
import { apiCall } from './services/api'; // Make sure this exists
import PaymentRedirectHandler from './components/PaymentRedirectHandler';
import Footer from './components/Footer';
import ContactPage from './pages/ContactPage';
import ResourceQuotaAlert from './components/subscription/ResourceQuotaAlert';




// Navigation component to handle auth redirects
const AuthNavigator = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      // Check for post-auth actions from landing page
      const postAuthAction = localStorage.getItem('postAuthAction');
      const selectedPlan = localStorage.getItem('selectedPlan');
      
      
      if (postAuthAction && selectedPlan) {
        
        // Clear the stored action immediately to prevent loops
        localStorage.removeItem('postAuthAction');
        
        if (postAuthAction === 'subscription' && selectedPlan !== 'plan_QmFREESalesWit') {
          // For paid plans, create subscription directly and redirect to payment
          handleDirectSubscriptionCreation(selectedPlan, user);
          return;
        } else if (postAuthAction === 'free_plan' || selectedPlan === 'plan_QmFREESalesWit') {
          // For free plan, just clean up and go to main app
          localStorage.removeItem('selectedPlan');
          navigate('/subscription-dashboard');
          return;
        }
      }
      
      // Normal authenticated user redirect logic
      if (location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/') {
        navigate('/subscription-dashboard');
      }
    } else if (!loading && !isAuthenticated) {
      // If user is not authenticated and trying to access protected routes
      const protectedRoutes = ['/create-cd', '/view-cds', '/profile-settings', '/view-cd', '/subscription-dashboard'];
      const isProtected = protectedRoutes.some(route => location.pathname.startsWith(route));
      
      if (isProtected) {
        window.location.href = '/landing.html';
      }
    }
  }, [isAuthenticated, loading, location.pathname, navigate, user]);

  // Function to handle direct subscription creation after auth
  const handleDirectSubscriptionCreation = async (planId, user) => {
    try {
      
      // Show loading indicator
      const loadingDiv = document.createElement('div');
      loadingDiv.id = 'payment-loading';
      loadingDiv.innerHTML = `
        <div style="
          position: fixed; 
          top: 0; 
          left: 0; 
          width: 100%; 
          height: 100%; 
          background: rgba(0,0,0,0.8); 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          z-index: 9999;
          color: white;
          font-family: Arial, sans-serif;
        ">
          <div style="text-align: center;">
            <div style="
              width: 40px; 
              height: 40px; 
              border: 4px solid #f3f3f3; 
              border-top: 4px solid #3498db; 
              border-radius: 50%; 
              animation: spin 1s linear infinite; 
              margin: 0 auto 20px;
            "></div>
            <div>Setting up your ${planId.replace('plan_Qm', '').replace('SalesWit', '')} subscription...</div>
            <div style="font-size: 14px; margin-top: 10px; opacity: 0.8;">
              You'll be redirected to payment in a moment
            </div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </div>
      `;
      document.body.appendChild(loadingDiv);
      
      // Calculate redirect URL for payment success
      const redirectUrl = window.location.origin + '/create-cd?from_payment=true&gateway=razorpay';
      
      // Get user ID in the right format
      const userId = user.uid || user.google_uid || user.id;
      
      // Create subscription via API with proper parameters
      const response = await apiCall('/api/subscriptions/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          plan_id: planId,
          app_id: 'saleswit',
          redirect_url: redirectUrl,
          customer_info: {
            email: user.email,
            first_name: user.displayName?.split(' ')[0] || 'User',
            last_name: user.displayName?.split(' ').slice(1).join(' ') || 'Name'
          }
        })
      });

      // Remove loading indicator
      if (document.getElementById('payment-loading')) {
        document.body.removeChild(loadingDiv);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const data = await response.json();

      // Clean up localStorage
      localStorage.removeItem('selectedPlan');

      // Handle the response
      if (data.subscription && data.subscription.short_url) {
        // Razorpay subscription with payment link
        window.location.href = data.subscription.short_url;
      } else if (data.result && data.result.approval_url) {
        // PayPal subscription requiring approval
        window.location.href = data.result.approval_url;
      } else if (data.subscription && data.subscription.status === 'active') {
        // Free plan or immediate activation
        navigate('/subscription-dashboard');
      } else {
        // Fallback - go to subscription dashboard
        navigate('/subscription-dashboard');
      }

    } catch (error) {
      console.error('Error creating subscription:', error);
      
      // Remove loading indicator if it exists
      const loadingDiv = document.getElementById('payment-loading');
      if (loadingDiv) {
        document.body.removeChild(loadingDiv);
      }
      
      // Clean up localStorage
      localStorage.removeItem('selectedPlan');
      localStorage.removeItem('postAuthAction');
      
      // Show error and redirect to subscription page
      alert('There was an error setting up your subscription. Please try again.');
      navigate('/subscription-dashboard');
    }
  };

  return children;
}
// Private route component (unchanged)
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-royal-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    window.location.href = '/landing.html';
    return null;
  }
  
  return children;
};

function AppContent() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [showSubscriptionModal, setShowSubscriptionModal] = React.useState(false);

  // Check if current page is an auth page
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';

  // Handle subscription management (not new subscriptions from landing)
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/subscription-dashboard') {
      const urlParams = new URLSearchParams(location.search);
      const action = urlParams.get('action');
      
      // Only show modal for management actions, not new subscriptions
      if (action === 'manage' || action === 'upgrade') {
        setShowSubscriptionModal(true);
        // Clean URL
        window.history.replaceState({}, document.title, location.pathname);
      }
    }
  }, [isAuthenticated, location]);

  return (
    <SubscriptionProvider user={user} appId="saleswit">
      <div className="flex flex-col min-h-screen">
        {/* Only show Header when NOT on auth pages */}
        {!isAuthPage && <Header />}
        
        <main className="flex-grow">
          <AuthNavigator>
            <Routes>
              {/* Public routes - Auth pages */}
              <Route path="/login" element={<Auth mode="login" />} />
              <Route path="/signup" element={<Auth mode="signup" />} />
              
              {/* Protected routes */}
              <Route path="/create-cd" element={
                <PrivateRoute>
                  <CreateCDPage />
                </PrivateRoute>
              } />
              <Route path="/payment-redirect" element={
                <PrivateRoute>
                  <PaymentRedirectHandler user={user} />
                </PrivateRoute>
              } />
              
              <Route path="/view-cds" element={
                <PrivateRoute>
                  <ViewCDsPage />
                </PrivateRoute>
              } />
              
              <Route path="/view-cd/:id" element={
                <PrivateRoute>
                  <ViewCDDetailPage />
                </PrivateRoute>
              } />
              <Route path="/contact" element={
                <PrivateRoute>
                  <ContactPage />
                </PrivateRoute>
              } />
              
              <Route path="/profile-settings" element={
                <PrivateRoute>
                  <ProfileSettingsPage user={user} />
                </PrivateRoute>
              } />
              
              {/* Subscription routes */}
              <Route path="/subscription-dashboard" element={
                <PrivateRoute>
                  <SubscriptionDashboard user={user} appId="saleswit" />
                </PrivateRoute>
              } />
              
              {/* Default route */}
              <Route path="/" element={<Navigate to="/create-cd" />} />
              
              {/* 404 route */}
              <Route path="*" element={
                <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                  <h1 className="text-4xl font-bold text-gray-800 mb-4">Page Not Found</h1>
                  <p className="text-lg text-gray-600 mb-8">The page you're looking for doesn't exist or has been moved.</p>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Go Home
                  </button>
                </div>
              } />
            </Routes>
          </AuthNavigator>
        </main>
        
        {/* Only show Footer when NOT on auth pages */}
        {!isAuthPage && <Footer />}
        
        {/* Add Resource Quota Alert */}
        {isAuthenticated && user && (
          <ResourceQuotaAlert user={user} />
        )}
        
        {/* Subscription Modal for management only */}
        {showSubscriptionModal && (
          <SubscriptionModal
            isOpen={showSubscriptionModal}
            onClose={() => setShowSubscriptionModal(false)}
            user={user}
            appId="saleswit"
            onSuccess={(message) => {
              setShowSubscriptionModal(false);
            }}
          />
        )}
      </div>
    </SubscriptionProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;